import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { api, ApiError } from '../api';

interface QueueItem {
  id: string;
  contactId: string;
  name: string;
  title: string | null;
  company: string | null;
  phone: string | null;
  isLead: boolean;
  attempts: number;
  lastCalledAt: string | null;
  nextAttemptAt: string | null;
  status: string;
  state: 'fresh' | 'followup' | 'callback' | 'waiting' | 'removed';
  score: number;
}
interface Kpis { total: number; fresh: number; followups: number; dueCallbacks: number; }
interface MyQueue { tab: string; kpis: Kpis; items: QueueItem[]; }
interface Outcome { id: string; name: string; }

type Tab = 'active' | 'waiting' | 'removed';
type Disp = 'done' | 'retry' | 'callback' | 'remove';

const OUTCOMES: { label: string; disp: Disp; dnc?: boolean }[] = [
  { label: 'Connected', disp: 'done' },
  { label: 'Left voicemail', disp: 'retry' },
  { label: 'No answer', disp: 'retry' },
  { label: 'Busy', disp: 'retry' },
  { label: 'Gatekeeper', disp: 'retry' },
  { label: 'Schedule callback', disp: 'callback' },
  { label: 'Wrong number', disp: 'remove' },
  { label: 'Not interested', disp: 'remove' },
  { label: 'Do not call', disp: 'remove', dnc: true },
];

interface Cadence { intervalsDays: number[]; maxAttempts: number; }
const CADENCE_KEY = 'crmhub.cadence';
const DEFAULT_CADENCE: Cadence = { intervalsDays: [2, 4, 7, 14], maxAttempts: 6 };
function loadCadence(): Cadence {
  try {
    const c = JSON.parse(localStorage.getItem(CADENCE_KEY) ?? '');
    if (Array.isArray(c?.intervalsDays) && c.intervalsDays.length) {
      return { intervalsDays: c.intervalsDays.map(Number).filter((n: number) => n > 0), maxAttempts: Number(c.maxAttempts) || 6 };
    }
  } catch { /* fall back to default */ }
  return DEFAULT_CADENCE;
}

const DAY = 86400000;
const telHref = (phone: string | null) => (phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : undefined);
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : null);

export function CallQueuePage() {
  const [tab, setTab] = useState<Tab>('active');
  const [data, setData] = useState<MyQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [modalItem, setModalItem] = useState<QueueItem | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cadence, setCadence] = useState<Cadence>(loadCadence);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.get<MyQueue>(`/api/call-queues/mine?tab=${tab}`));
    } catch (e) {
      setError(e instanceof ApiError ? `Couldn't load your queue (${e.status}).` : "Couldn't load your queue.");
    }
  }, [tab]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setSkipped(new Set()); }, [tab]);
  useEffect(() => { void api.get<Outcome[]>('/api/activity-outcomes').then(setOutcomes).catch(() => undefined); }, []);

  const outcomeIdByName = useMemo(() => new Map(outcomes.map((o) => [o.name.toLowerCase(), o.id])), [outcomes]);

  const visible = useMemo(() => {
    let rows = (data?.items ?? []).filter((r) => !skipped.has(r.id));
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) => [r.name, r.company, r.phone, r.title].some((v) => v?.toLowerCase().includes(q)));
    return rows;
  }, [data, skipped, search]);

  async function hide(item: QueueItem) {
    try { await api.post(`/api/call-queues/mine/${item.id}/hide`); await load(); } catch { /* ignore */ }
  }

  async function saveOutcome(item: QueueItem, o: (typeof OUTCOMES)[number], durationMin: string, notes: string, callbackAt: string) {
    let disposition: Disp | 'done' = o.disp;
    let nextAttemptAt: string | undefined;
    if (o.disp === 'retry') {
      if (item.attempts + 1 >= cadence.maxAttempts) {
        disposition = 'done'; // cadence exhausted — stop retrying
      } else {
        const idx = Math.min(item.attempts, cadence.intervalsDays.length - 1);
        nextAttemptAt = new Date(Date.now() + cadence.intervalsDays[idx] * DAY).toISOString();
      }
    } else if (o.disp === 'callback') {
      if (!callbackAt) { window.alert('Pick a callback date/time.'); return; }
      nextAttemptAt = new Date(callbackAt).toISOString();
    }
    const mins = Number(durationMin);
    await api.post(`/api/call-queues/mine/${item.id}/outcome`, {
      outcomeId: outcomeIdByName.get(o.label.toLowerCase()),
      outcomeLabel: o.label,
      disposition,
      notes: notes || undefined,
      durationSeconds: durationMin && Number.isFinite(mins) ? Math.round(mins * 60) : undefined,
      nextAttemptAt,
      markDoNotCall: o.dnc || undefined,
    });
    setModalItem(null);
    await load();
  }

  const k = data?.kpis;
  return (
    <div className="card">
      <h1 style={{ marginBottom: 2 }}>My Call Queue</h1>
      <p className="muted" style={{ marginTop: 0 }}>Phone outreach to your owned leads + contacts, prioritized. Dials through Zoom Phone.</p>

      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border,#e5e7eb)', marginBottom: '1rem' }}>
        <div className="row" style={{ gap: '1.25rem' }}>
          {(['active', 'waiting', 'removed'] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} style={tabStyle(tab === t)}>
              {t === 'active' ? 'Active queue' : t === 'waiting' ? '⏳ Waiting / Contacted' : 'Removed'}
            </button>
          ))}
        </div>
        <button onClick={() => setSettingsOpen(true)} style={{ ...tabStyle(false), color: 'var(--accent,#1d4ed8)' }}>⚙ My Cadence Settings</button>
      </div>

      <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
        <Kpi label="Total in queue" value={k?.total ?? 0} />
        <Kpi label="Due callbacks" value={k?.dueCallbacks ?? 0} />
        <Kpi label="Fresh (untouched)" value={k?.fresh ?? 0} />
        <Kpi label="Follow-ups" value={k?.followups ?? 0} />
      </div>

      <input
        placeholder="Search name / company / phone…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginTop: '1rem', maxWidth: 380, width: '100%' }}
      />

      {error && <p className="empty" style={{ marginTop: '1rem' }}>{error}</p>}

      <div className="card" style={{ marginTop: '1rem', padding: 0 }}>
        {visible.length === 0 ? (
          <p className="empty" style={{ padding: '2rem', textAlign: 'center' }}>
            {tab === 'active'
              ? 'No one to call right now. Own some contacts (set yourself as owner on the Contacts page) or wait for callbacks to come due.'
              : tab === 'waiting'
                ? 'Nobody waiting — contacted leads with a future follow-up will appear here.'
                : 'Nothing removed.'}
          </p>
        ) : (
          visible.map((it) => (
            <div key={it.id} className="row" style={rowStyle}>
              <div style={{ flex: '1 1 320px', minWidth: 240 }}>
                <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
                  <strong>{it.name}</strong>
                  <StateBadge state={it.state} />
                  {it.isLead && <span style={leadBadge}>lead</span>}
                  <span className="muted" style={{ fontSize: '0.8rem' }}>score {it.score}</span>
                </div>
                <div className="muted" style={{ fontSize: '0.85rem' }}>
                  {[it.title, it.company].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <div style={{ flex: '0 0 200px' }}>
                <div>{it.phone ?? '— no phone —'}</div>
                <div className="muted" style={{ fontSize: '0.8rem' }}>
                  {it.state === 'callback' && it.nextAttemptAt ? `Callback ${fmt(it.nextAttemptAt)}`
                    : it.lastCalledAt ? `Last called ${fmt(it.lastCalledAt)}`
                    : 'Never called'}
                </div>
              </div>
              <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
                {telHref(it.phone) ? (
                  <a className="btn btn-primary" href={telHref(it.phone)} onClick={() => setModalItem(it)} style={{ textDecoration: 'none' }}>📞 Call via Zoom</a>
                ) : (
                  <button className="btn" disabled>No phone</button>
                )}
                <button className="btn" onClick={() => setModalItem(it)}>Log outcome</button>
                <button className="btn" title="Skip for now" onClick={() => setSkipped((s) => new Set(s).add(it.id))}>⏭</button>
                <button className="btn" title="Remove from queue" onClick={() => void hide(it)}>🚫</button>
              </div>
            </div>
          ))
        )}
      </div>

      <p className="muted" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
        📞 Zoom Phone required for click-to-dial. If Zoom isn't installed, your browser falls back to the default <code>tel:</code> handler.
      </p>

      {modalItem && (
        <LogCallModal item={modalItem} onClose={() => setModalItem(null)} onSave={saveOutcome} />
      )}
      {settingsOpen && (
        <CadenceModal
          cadence={cadence}
          onClose={() => setSettingsOpen(false)}
          onSave={(c) => { setCadence(c); localStorage.setItem(CADENCE_KEY, JSON.stringify(c)); setSettingsOpen(false); }}
        />
      )}
    </div>
  );
}

function LogCallModal({ item, onClose, onSave }: { item: QueueItem; onClose: () => void; onSave: (item: QueueItem, o: (typeof OUTCOMES)[number], durationMin: string, notes: string, callbackAt: string) => void | Promise<void> }) {
  const [picked, setPicked] = useState<(typeof OUTCOMES)[number] | null>(null);
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  return (
    <div style={overlay} onClick={onClose}>
      <div className="card" style={{ maxWidth: 540, width: '100%', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '1.05rem', margin: 0 }}>Log call — {item.name}</h2>
          <button className="btn" onClick={onClose}>✕</button>
        </div>
        <p className="muted">Attempt {item.attempts + 1}. What happened on this call?</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
          {OUTCOMES.map((o) => (
            <button key={o.label} className="btn" onClick={() => setPicked(o)}
              style={{ borderColor: picked?.label === o.label ? 'var(--accent,#1d4ed8)' : undefined, fontWeight: picked?.label === o.label ? 700 : 400, color: o.dnc ? 'var(--danger,#c0392b)' : undefined }}>
              {o.label}
            </button>
          ))}
        </div>
        {picked?.disp === 'callback' && (
          <label className="stack" style={{ marginTop: '0.75rem' }}>
            <span className="muted">Callback date / time</span>
            <input type="datetime-local" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} />
          </label>
        )}
        <div className="row" style={{ marginTop: '0.75rem', gap: '1rem' }}>
          <label className="stack" style={{ flex: '0 0 140px' }}><span className="muted">Duration (min)</span>
            <input value={duration} onChange={(e) => setDuration(e.target.value)} inputMode="numeric" placeholder="0" />
          </label>
        </div>
        <label className="stack" style={{ marginTop: '0.75rem' }}><span className="muted">Notes</span>
          <textarea placeholder="What did they say? Any next steps?" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!picked} onClick={() => picked && void onSave(item, picked, duration, notes, callbackAt)}>Save outcome</button>
        </div>
      </div>
    </div>
  );
}

function CadenceModal({ cadence, onClose, onSave }: { cadence: Cadence; onClose: () => void; onSave: (c: Cadence) => void }) {
  const [intervals, setIntervals] = useState(cadence.intervalsDays.join(', '));
  const [maxAttempts, setMaxAttempts] = useState(String(cadence.maxAttempts));
  return (
    <div style={overlay} onClick={onClose}>
      <div className="card" style={{ maxWidth: 460, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '1.05rem', margin: 0 }}>My Cadence Settings</h2>
          <button className="btn" onClick={onClose}>✕</button>
        </div>
        <p className="muted">Days between follow-up attempts (no-answer, voicemail, busy…). Saved on this device.</p>
        <label className="stack"><span className="muted">Follow-up intervals (days, comma-separated)</span>
          <input value={intervals} onChange={(e) => setIntervals(e.target.value)} placeholder="2, 4, 7, 14" />
        </label>
        <label className="stack" style={{ marginTop: '0.75rem' }}><span className="muted">Max attempts before auto-closing</span>
          <input value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} inputMode="numeric" />
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => {
            const days = intervals.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
            onSave({ intervalsDays: days.length ? days : DEFAULT_CADENCE.intervalsDays, maxAttempts: Math.max(1, Number(maxAttempts) || DEFAULT_CADENCE.maxAttempts) });
          }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="card" style={{ flex: '1 1 200px', minWidth: 180 }}>
      <div className="muted" style={{ fontSize: '0.85rem' }}>{label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function StateBadge({ state }: { state: QueueItem['state'] }) {
  const map: Record<QueueItem['state'], [string, string, string]> = {
    fresh: ['Fresh', '#eef2ff', '#3730a3'],
    followup: ['Follow-up', '#fff7ed', '#9a3412'],
    callback: ['Callback due', '#ecfdf5', '#065f46'],
    waiting: ['Waiting', '#f3f4f6', '#374151'],
    removed: ['Removed', '#fef2f2', '#991b1b'],
  };
  const [label, bg, fg] = map[state];
  return <span style={{ background: bg, color: fg, borderRadius: 6, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600 }}>{label}</span>;
}

const leadBadge: CSSProperties = { border: '1px solid var(--border,#cbd5e1)', borderRadius: 6, padding: '0 7px', fontSize: '0.72rem', color: '#475569' };
const rowStyle: CSSProperties = { justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', padding: '0.85rem 1rem', borderBottom: '1px solid var(--border,#eef0f3)' };
const overlay: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' };
function tabStyle(active: boolean): CSSProperties {
  return {
    background: 'none',
    border: 'none',
    borderBottom: active ? '2px solid var(--accent,#1d4ed8)' : '2px solid transparent',
    color: active ? 'var(--accent,#1d4ed8)' : 'inherit',
    fontWeight: active ? 700 : 400,
    padding: '0.6rem 0.15rem',
    cursor: 'pointer',
  };
}
